import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { AuthContext } from '../../../context/AuthContext';
import { Button } from '../../common/Button';

const SignUpContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  justify-content: center;
`;

const Title = styled.h1`
  font-size: 28px;
  color: ${theme.colors.deepNavy};
  text-align: center;
  margin-bottom: ${theme.spacing.xs};
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.slateGray};
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSizes.medium};
  margin-bottom: ${theme.spacing.xs};
  color: ${theme.colors.deepNavy};
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.softGray};
  border-radius: ${theme.borderRadius.medium};
  font-size: ${theme.typography.fontSizes.medium};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.brandYellow};
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${theme.spacing.sm};
`;

const Checkbox = styled.input`
  margin-right: ${theme.spacing.sm};
`;

const CheckboxLabel = styled.label`
  font-size: ${theme.typography.fontSizes.small};
  color: ${theme.colors.slateGray};
`;

const Link = styled.span`
  color: ${theme.colors.deepNavy};
  font-weight: bold;
  cursor: pointer;
`;

const ErrorMessage = styled.p`
  color: #c62828;
  font-size: ${theme.typography.fontSizes.small};
  margin-top: ${theme.spacing.xs};
`;

const LoginLink = styled.p`
  text-align: center;
  margin-top: ${theme.spacing.lg};
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.slateGray};
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: ${theme.spacing.lg} 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${theme.colors.softGray};
  }
`;

const DividerText = styled.span`
  padding: 0 ${theme.spacing.md};
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.slateGray};
`;

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  width: 100%;
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.softGray};
  border-radius: ${theme.borderRadius.medium};
  font-size: ${theme.typography.fontSizes.medium};
  font-family: ${theme.typography.bodyFont};
  color: ${theme.colors.deepNavy};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${theme.colors.background};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.0 24.0 0 0 0 0 21.56l7.98-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

export const SignUpScreen = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [signUpError, setSignUpError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const { signup, signInWithGoogle, error } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSignUpError('');

    if (!email || !fullName || !password || !confirmPassword) {
      setSignUpError(t('auth.fillAllFields'));
      return;
    }

    if (password !== confirmPassword) {
      setSignUpError(t('auth.passwordMismatch'));
      return;
    }

    if (!agreeToTerms) {
      setSignUpError(t('auth.mustAgreeTerms'));
      return;
    }

    try {
      await signup(email, password, { fullName });
      navigate('/');
    } catch (error) {
      console.error('Signup failed:', error);
      setSignUpError(t('auth.failedCreateAccount'));
    }
  };

  const handleGoogleSignUp = async () => {
    setSignUpError('');
    setGoogleLoading(true);
    try {
      const result = await signInWithGoogle();
      if (!result) return; // User closed popup
      navigate('/');
    } catch (error) {
      if (error.code === 'auth/account-exists-with-different-credential') {
        setSignUpError(t('auth.accountExistsWithDifferentCredential'));
      } else {
        setSignUpError(t('auth.googleSignInError'));
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <SignUpContainer>
      <Title>{t('auth.signUpTitle')}</Title>
      <Subtitle>{t('auth.signUpSubtitle')}</Subtitle>
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="email">{t('auth.email')}</Label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@example.com"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="fullName">{t('auth.displayName')}</Label>
          <Input
            type="text"
            id="fullName"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder={t('auth.namePlaceholder')}
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="password">{t('auth.password')}</Label>
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
          <Input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
          />
        </FormGroup>

        <CheckboxContainer>
          <Checkbox 
            type="checkbox" 
            id="terms" 
            checked={agreeToTerms}
            onChange={(e) => setAgreeToTerms(e.target.checked)}
          />
          <CheckboxLabel htmlFor="terms">
            {t('auth.agreeToTerms')} <Link onClick={() => navigate('/terms-of-service')}>{t('auth.termsOfService')}</Link> {t('auth.and')} <Link onClick={() => navigate('/privacy-policy')}>{t('auth.privacyPolicy')}</Link>
          </CheckboxLabel>
        </CheckboxContainer>
        
        {(signUpError || error) && (
          <ErrorMessage>{signUpError || error}</ErrorMessage>
        )}
        
        <Button primary type="submit">
          {t('auth.createAccount')}
        </Button>
      </Form>

      <Divider>
        <DividerText>{t('auth.or')}</DividerText>
      </Divider>

      <GoogleButton onClick={handleGoogleSignUp} disabled={googleLoading}>
        <GoogleIcon />
        {t('auth.signUpWithGoogle')}
      </GoogleButton>

      <LoginLink>
        {t('auth.hasAccount')} <Link onClick={() => navigate('/login')}>{t('auth.logIn')}</Link>
      </LoginLink>
    </SignUpContainer>
  );
};
